import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { FACTORY_SLIDES, waLink } from "@/data/site";

const STATS = [
  { value: "15+", label: "Tahun Pengalaman" },
  { value: "50.000+", label: "m3 Kayu Terjual" },
  { value: "100%", label: "Legal & Bersertifikat SVLK" },
];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % FACTORY_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="beranda"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-center overflow-hidden"
      style={{ backgroundColor: "#041B2E" }}
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide}
            src={FACTORY_SLIDES[slide]}
            alt="Pabrik kayu olahan dan pabrik triplek PT. SINAR AL MUBAROKAH"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0 h-[115%] w-full object-cover"
          />
        </AnimatePresence>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(102deg, rgba(4,27,46,0.95) 0%, rgba(10,42,67,0.72) 48%, rgba(16,64,95,0.38) 100%)",
          }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-6 pb-28 pt-36 lg:grid-cols-12 lg:px-8 lg:pt-40">
        <div className="lg:col-span-12">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="overline-label mb-6 flex items-center gap-3"
          >
            <span className="inline-block h-px w-10" style={{ backgroundColor: "var(--t-accent)" }} />
            Distributor Kayu Olahan & Triplek Indonesia
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-[#F7EEDD] sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            <MaskedLine delay={0.3}>PT. SINAR</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="italic" style={{ color: "var(--t-accent)" }}>
                AL MUBAROKAH
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#EBDFC9] sm:text-lg"
          >
            Pusat <strong className="text-[#FBF1DE]">distributor triplek murah</strong> dan{" "}
            <strong className="text-[#FBF1DE]">jual kayu meranti berkualitas</strong> langsung dari
            pabrik. Melayani <strong className="text-[#FBF1DE]">grosir kayu jati</strong>, multiplek,
            blockboard, hingga kayu konstruksi proyek — stok melimpah, harga distributor, siap
            kirim ke seluruh Indonesia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              style={{
                backgroundColor: "var(--t-accent)",
                color: "var(--t-dark)",
                boxShadow: "0 12px 40px -10px color-mix(in srgb, var(--t-accent) 55%, transparent)",
              }}
            >
              <MessageCircle size={18} className="transition-transform duration-300 group-hover:rotate-12" />
              Minta Penawaran via WhatsApp
            </a>
            <a
              href="#katalog"
              data-testid="hero-catalog-cta"
              className="inline-flex items-center gap-3 rounded-full border border-[#D9B98A]/40 px-7 py-4 text-sm font-semibold text-[#F3E9D6] transition-colors duration-300 hover:border-[#D9B98A]/80 hover:bg-[#D9B98A]/10"
            >
              Lihat Katalog 20 Produk
              <ArrowDown size={16} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-14 grid max-w-xl grid-cols-3 gap-6 border-t border-[#D9B98A]/25 pt-7"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-serif text-3xl font-semibold sm:text-4xl" style={{ color: "var(--t-accent)" }}>
                  {s.value}
                </p>
                <p className="mt-1 text-xs uppercase tracking-wider text-[#D9C5A8]">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

      </div>
    </section>
  );
};
